/* Home Gym v2. Cart = selection state. ES2018, native theme drawers, no dependencies. */
(() => {
  if (window.hpHomegymInitialized || !document.querySelector('[data-homegym-config]')) return;
  window.hpHomegymInitialized = true;
  let config;
  let cart;
  let readPromise;
  let timer;
  let eventVersion = 0;
  let busy = false;
  let pendingRefresh = false;
  let queue = Promise.resolve();
  let quickContext = null;
  let choiceContext = null;
  let galleryContext = null;
  let layoutObserver;
  let headerObserver;
  let scrollFrame;
  let infoContext = null;
  let firstRenderDone = false;
  const rowCache = new WeakMap();
  const chipPopTimers = new WeakMap();
  const manualAccessories = new WeakSet();
  const productCache = new Map();
  const preferredVariants = new Map();
  const rootSelector = '[data-homegym-steps], [data-homegym-pdp]';
  const dialogSelector = '[data-homegym-gallery], [data-homegym-info], [data-homegym-replace]';
  const all = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const one = (selector, root = document) => root.querySelector(selector);
  const rows = () => all('[data-homegym-row]').filter((row) => row.closest(rootSelector));
  const str = (value) => String(value);
  const text = (key, values = {}) => Object.keys(values).reduce((value, name) => (
    value.split(`__${name.toUpperCase()}__`).join(str(values[name]))
  ), config.texts[key] || '');
  const money = (cents) => {
    const parts = (Number(cents || 0) / 100).toFixed(2).split('.');
    return `${config.currency || 'CHF'} ${parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, "'")}.${parts[1]}`;
  };
  const setText = (selector, value, root = document) => {
    const el = one(selector, root);
    if (el) el.textContent = value;
  };
  const rowData = (row) => {
    if (!rowCache.has(row)) rowCache.set(row, JSON.parse(row.dataset.row));
    return rowCache.get(row);
  };
  const linesFor = (row) => (cart ? cart.items : []).filter((line) => (
    str(line.product_id) === row.dataset.productId
    && (!row.dataset.fixedVariant || str(line.variant_id) === row.dataset.fixedVariant)
  ));
  const make = (tag, className, value) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (value !== undefined) element.textContent = value;
    return element;
  };
  function showError(row, error) {
    setText('[data-homegym-error]', error && error.message ? error.message : str(error || ''), row);
  }
  async function request(url, body) {
    const opts = { credentials: 'same-origin', headers: { Accept: 'application/json' } };
    if (body) {
      opts.method = 'POST';
      if (body instanceof FormData) opts.body = body;
      else {
        opts.headers['Content-Type'] = 'application/json';
        opts.body = JSON.stringify(body);
      }
    } else opts.cache = 'no-store';
    const response = await fetch(url, opts);
    const data = await response.json().catch(() => null);
    if (!response.ok || !data || data.status >= 400) {
      const error = new Error(data && typeof data.description === 'string' ? data.description : text('error'));
      error.status = response.status;
      throw error;
    }
    return data;
  }
  // One in-flight GET. Signals during a read produce at most one later read if needed.
  function readCart() {
    if (!readPromise) {
      readPromise = request(config.cartUrl).finally(() => { readPromise = null; });
    }
    return readPromise;
  }
  async function refresh() {
    if (busy) { pendingRefresh = true; return; }
    const version = eventVersion;
    try {
      const fresh = await readCart();
      if (!busy && version === eventVersion) render(fresh);
      setText('[data-homegym-sync-error]', '');
    } catch (error) {
      setText('[data-homegym-sync-error]', text('cart_error'));
    }
  }
  function scheduleRefresh(event) {
    if (event && event.detail && event.detail.hpHomegymCart) return;
    eventVersion += 1;
    clearTimeout(timer);
    if (busy) { pendingRefresh = true; return; }
    timer = setTimeout(refresh, 100);
  }
  // Der Cart-Drawer rendert nach unseren add/change-Aufrufen nicht von selbst neu (das Refresh-Event des
  // Themes loest bei einem beim Laden leeren Warenkorb keinen Section-Fetch aus, Kritikrunde 2 v2.1):
  // deshalb den Drawer direkt per refresh(true) neu laden.
  let drawerRefresh = Promise.resolve();
  function notify(fresh) {
    render(fresh);
    const cartDrawer = one('cart-drawer');
    if (cartDrawer && typeof cartDrawer.refresh === 'function') {
      drawerRefresh = Promise.resolve(cartDrawer.refresh(true)).catch(() => null);
    } else {
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { detail: { hpHomegymCart: fresh } }));
    }
  }
  function getProduct(row) {
    const data = rowData(row);
    if (!productCache.has(data.handle)) {
      const root = config.rootUrl.replace(/\/?$/, '/');
      const entry = { value: null };
      entry.promise = request(`${root}products/${encodeURIComponent(data.handle)}.js`)
        .then((product) => { entry.value = product; if (cart) render(cart); return product; })
        .catch(() => { productCache.delete(data.handle); return null; });
      productCache.set(data.handle, entry);
    }
    return productCache.get(data.handle);
  }
  function variantFor(row, id) {
    const data = rowData(row);
    const cache = productCache.get(data.handle);
    const variants = cache && cache.value ? cache.value.variants : data.variants;
    return variants.find((variant) => str(variant.id) === str(id));
  }
  function paintDeliveryTiers() {
    const core = window.HPDeliveryCore;
    if (!core || typeof core.lookupLabel !== 'function') return;
    all('[data-homegym-steps] [data-hp-tier], [data-homegym-pdp] [data-hp-tier], .hp-pdp-set [data-hp-tier]').forEach((line) => {
      const meta = core.lookupLabel(line.dataset.hpTier);
      if (!meta) return;
      line.style.color = meta.color || '';
      all('svg', line).forEach((icon) => { icon.style.color = meta.icon_color || meta.color || ''; });
    });
  }
  function renderDelivery(row, variant) {
    const data = rowData(row);
    const box = one('[data-homegym-delivery]', row);
    const entry = (data.delivery || {})[str(variant.id)];
    let buckets = entry && entry.stock_breakdown;
    if ((!buckets || !buckets.length) && data.buckets) {
      buckets = (data.buckets[str(variant.id)] || []).map(([label, qty]) => ({ label_key: label, qty }));
    }
    const fastest = (buckets || []).filter((bucket) => (
      config.deliveryOrder.includes(bucket.label_key) && (Number(bucket.qty) > 0 || bucket.label_key === 'made_to_order')
    )).sort((a, b) => config.deliveryOrder.indexOf(a.label_key) - config.deliveryOrder.indexOf(b.label_key))[0];
    box.textContent = '';
    if (variant.available === false || !fastest) {
      const soldOutKey = data.discontinued ? 'sold_out' : 'sold_out_planned';
      box.textContent = text(variant.available === false ? soldOutKey : 'delivery_unknown');
    } else {
      let label = text(`delivery.${fastest.label_key}`);
      if (fastest.label_key === 'made_to_order' && fastest.months_min != null) {
        label += ` ${text('delivery.mto_suffix', { min: fastest.months_min, max: fastest.months_max })}`;
      }
      const line = make('span', 'hp-homegym-delivery-line');
      line.dataset.hpTier = fastest.label_key;
      line.appendChild(one('[data-homegym-in-cart] svg', row).cloneNode(true));
      line.appendChild(make('span', '', label));
      box.appendChild(line);
    }
    box.title = box.textContent;
  }
  function renderPrice(row, variant, unchosen) {
    const data = rowData(row);
    const box = one('[data-homegym-price]', row);
    const price = variant.price;
    const compare = variant.compare_at_price || 0;
    const amount = unchosen ? data.discount : (compare > price && price > 0 ? Math.round((compare - price) * 100 / compare) : 0);
    box.textContent = '';
    box.appendChild(make('span', 'price__current', `${unchosen && data.priceVaries ? `${text('from')} ` : ''}${money(price)}`));
    if (compare > price) box.appendChild(make('s', 'price__was', money(compare)));
    const badge = one('[data-homegym-discount]', row);
    badge.hidden = amount <= 0;
    badge.textContent = text(unchosen ? 'discount_up_to' : 'discount', { amount });
  }
  function renderLines(row, lines) {
    const data = rowData(row);
    const container = one('[data-homegym-lines]', row);
    const signature = JSON.stringify(lines.map((line) => [line.key, line.quantity, line.variant_title, line.final_line_price]));
    container.hidden = !lines.length;
    if (container.dataset.signature === signature) return;
    // Keep keyboard focus when the cart changes its keys or removes the focused line.
    const active = document.activeElement;
    const focused = container.contains(active);
    const oldLine = focused && active.closest('[data-line-key]');
    const oldIndex = oldLine ? all('[data-line-key]', container).indexOf(oldLine) : 0;
    const action = focused && active.dataset.homegymQuantity;
    const isInput = focused && active.hasAttribute('data-homegym-quantity-input');
    container.textContent = '';
    container.dataset.signature = signature;
    lines.forEach((line) => {
      const group = make('div', 'hp-homegym-cart-line');
      group.dataset.lineKey = line.key;
      group.dataset.variantId = str(line.variant_id);
      if (data.multi && lines.length > 1) group.appendChild(make('small', '', `${line.variant_title || ''} · ${money(line.final_line_price)}`));
      // Compact rows mirror the Liquid markup: theme quantity field (as in the SliderCart) with a typed number.
      const compact = row.classList.contains('hp-homegym-row--compact');
      const controls = make('div', compact ? 'hp-homegym-qty qty-input qty-input--combined' : 'hp-homegym-quantity');
      const minus = make('button', compact ? 'qty-input__btn btn--minus' : '', compact ? '' : '−');
      minus.type = 'button'; minus.dataset.homegymQuantity = '-1'; minus.setAttribute('aria-label', text('decrease'));
      controls.appendChild(minus);
      if (data.quantityInput || compact) {
        const input = make('input', compact ? 'qty-input__input input' : ''); input.type = compact ? 'number' : 'text'; input.inputMode = 'numeric'; input.pattern = '[0-9]*';
        if (compact) input.min = '0';
        input.value = line.quantity; input.dataset.homegymQuantityInput = ''; input.setAttribute('aria-label', text('quantity'));
        controls.appendChild(input);
      } else controls.appendChild(make('span', '', line.quantity));
      const plus = make('button', compact ? 'qty-input__btn btn--plus' : '', compact ? '' : '+');
      plus.type = 'button'; plus.dataset.homegymQuantity = '1'; plus.setAttribute('aria-label', text('increase'));
      const variant = variantFor(row, line.variant_id);
      plus.disabled = !!variant && variant.available === false;
      controls.appendChild(plus); group.appendChild(controls); container.appendChild(group);
    });
    if (focused) {
      const group = container.children[Math.min(oldIndex, lines.length - 1)];
      const focus = group && one(isInput ? 'input' : `[data-homegym-quantity="${action}"]`, group);
      (focus || one('[data-homegym-choose], [data-homegym-add]', row) || row).focus({ preventScroll: true });
    }
  }
  function renderRow(row) {
    const data = rowData(row);
    const lines = linesFor(row);
    const preferred = preferredVariants.get(row.dataset.productId);
    const selected = lines.find((line) => str(line.variant_id) === preferred) || lines[0];
    const base = data.variants.find((variant) => str(variant.id) === row.dataset.variantId) || data.variants[0];
    const variant = selected ? (variantFor(row, selected.variant_id) || base) : base;
    const unchosen = data.multi && !selected;
    const soldOut = !data.multi && variant.available === false;
    const notifyable = soldOut && !data.discontinued;
    row.classList.toggle('is-in-cart', lines.length > 0);
    row.classList.toggle('is-sold-out', soldOut);
    one('[data-homegym-in-cart]', row).hidden = !lines.length;
    const bell = one('[data-homegym-bell]', row);
    if (bell) bell.hidden = !notifyable || lines.length > 0;
    const notifyButton = one('[data-homegym-notify]', row);
    if (notifyButton) notifyButton.hidden = !notifyable || lines.length > 0;
    const add = one('[data-homegym-add]', row);
    if (add) { add.hidden = lines.length > 0 || notifyable; add.disabled = !data.available; }
    setText('[data-homegym-choose-label]', text(lines.length ? 'change' : 'choose_options'), row);
    const chooseCaption = one('[data-homegym-choose-caption]', row);
    if (chooseCaption) chooseCaption.hidden = lines.length > 0;
    setText('[data-homegym-variant]', (selected || row.dataset.fixedVariant) && variant.title !== 'Default Title' ? variant.title : '', row);
    const spec = one('.hp-homegym-row__spec', row);
    if (spec) spec.title = Array.from(spec.children).map((part) => part.textContent.trim()).filter(Boolean).join(' | ');
    renderDelivery(row, variant);
    renderPrice(row, unchosen ? { price: data.basePrice, compare_at_price: data.baseCompare } : variant, unchosen);
    const quantity = lines.reduce((sum, line) => sum + line.quantity, 0) || 1;
    const total = lines.length ? lines.reduce((sum, line) => sum + line.final_line_price, 0) : variant.price;
    setText('[data-homegym-area]', text(data.area > 0 ? 'area_summary' : 'line_summary', {
      quantity, area: Number((quantity * data.area).toFixed(4)), amount: money(total)
    }), row);
    renderLines(row, lines);
    if (selected && data.multi) getProduct(row);
  }
  function render(fresh) {
    cart = fresh;
    rows().forEach(renderRow);
    paintDeliveryTiers();
    all('[data-homegym-steps] [data-homegym-accessories-toggle]').forEach((details) => {
      const product = details.closest('.hp-homegym-product');
      const mainRow = product && one('[data-homegym-main-row]', product);
      if (!mainRow) return;
      const inCart = linesFor(mainRow).length > 0;
      const cartState = str(inCart);
      const previous = details.dataset.cartState;
      // Vor dem ersten Cart-Read gilt ein manueller Summary-Klick ebenfalls.
      const sync = previous === undefined ? !manualAccessories.has(details) : previous !== cartState;
      if (sync && details.open !== inCart) {
        details.open = inCart;
        scheduleLayout();
      }
      details.dataset.cartState = cartState;
    });
    all('[data-homegym-steps] [data-homegym-step]').forEach((step) => {
      const names = all('[data-homegym-row]', step).filter((row) => linesFor(row).length).map((row) => rowData(row).title);
      const label = names.length ? text('chosen', { name: Array.from(new Set(names)).join(', ') }) : '';
      setText('[data-homegym-chosen-label]', label, step);
      one('[data-homegym-chosen-icon]', step).hidden = !names.length;
      one('[data-homegym-chosen]', step).title = label;
      const root = step.closest('[data-homegym-steps]');
      const chip = all('[data-homegym-chip]', root).find((el) => el.dataset.homegymChip === step.dataset.homegymStep);
      if (chip) {
        const wasInCart = chip.classList.contains('is-in-cart');
        chip.classList.toggle('is-in-cart', names.length > 0);
        if (firstRenderDone && !wasInCart && names.length > 0) {
          clearTimeout(chipPopTimers.get(chip));
          chip.classList.add('is-just-added');
          chipPopTimers.set(chip, setTimeout(() => {
            chip.classList.remove('is-just-added');
            chipPopTimers.delete(chip);
          }, 400));
        } else if (!names.length) {
          clearTimeout(chipPopTimers.get(chip));
          chipPopTimers.delete(chip);
          chip.classList.remove('is-just-added');
        }
        if (label) chip.setAttribute('aria-label', `${one('[data-homegym-chip-label]', chip).textContent.trim()}: ${label}`);
        else chip.removeAttribute('aria-label');
      }
    });
    firstRenderDone = true;
    scheduleLayout();
  }
  // Serialize mutations, including errors. Older reads cannot paint over a POST.
  function mutate(row, work) {
    row.setAttribute('aria-busy', 'true');
    queue = queue.then(async () => {
      busy = true; eventVersion += 1; clearTimeout(timer); showError(row, '');
      try {
        if (readPromise) await readPromise.catch(() => null);
        if (!cart) cart = await readCart();
        const fresh = await work();
        if (fresh) notify(fresh);
      } catch (error) {
        showError(row, error);
        try { notify(await readCart()); } catch (readError) { setText('[data-homegym-sync-error]', text('cart_error')); }
      } finally {
        row.removeAttribute('aria-busy'); busy = false;
        if (pendingRefresh) { pendingRefresh = false; scheduleRefresh(); }
      }
    });
    return queue;
  }
  function otherRacks(row) {
    if (!row.closest('[data-homegym-steps]') || row.closest('[data-homegym-pdp]')) return [];
    const step = row.closest('[data-homegym-step]');
    if (!step || step.dataset.single !== 'true' || row.dataset.fixedVariant) return [];
    const ids = new Set(all('[data-homegym-main-row]', step).map((el) => el.dataset.productId));
    return cart.items.filter((line) => ids.has(str(line.product_id)) && str(line.product_id) !== row.dataset.productId);
  }
  function finishChoice(mode) {
    if (!choiceContext) return;
    const { dialog, opener, resolve } = choiceContext;
    choiceContext = null; dialog.hidden = true; opener.focus({ preventScroll: true }); resolve(mode);
  }
  async function chooseRack(row, opener) {
    if (!row.closest('[data-homegym-steps]') || row.closest('[data-homegym-pdp]')) return 'append';
    await queue;
    if (!cart) { cart = await readCart(); render(cart); }
    if (!otherRacks(row).length || linesFor(row).length) return 'append';
    if (choiceContext) finishChoice('cancel');
    return new Promise((resolve) => {
      const dialog = one('[data-homegym-replace]', row);
      choiceContext = { dialog, opener, resolve };
      dialog.hidden = false;
      one('button', dialog).focus();
    });
  }
  async function removeRacks(row, oldLines) {
    const updates = {};
    oldLines.forEach((line) => { updates[str(line.variant_id)] = 0; });
    if (!Object.keys(updates).length) return readCart();
    // Use variant IDs: the warehouse annotator may have changed line keys since add.js.
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const fresh = await request(config.updateUrl, { updates });
        if (!fresh.items.some((line) => Object.prototype.hasOwnProperty.call(updates, str(line.variant_id)))) return fresh;
      } catch (error) { if (attempt === 1) throw new Error(text('replace_error')); }
    }
    throw new Error(text('replace_error'));
  }
  async function addRow(row, opener) {
    try {
      const mode = await chooseRack(row, opener);
      if (mode === 'cancel') return;
      await mutate(row, async () => {
        const old = mode === 'replace' ? otherRacks(row) : [];
        await request(config.addUrl, { items: [{ id: Number(row.dataset.fixedVariant || row.dataset.variantId), quantity: 1 }] });
        const fresh = old.length ? await removeRacks(row, old) : await readCart();
        // The plus becomes hidden after adding: return keyboard focus to its quantity control.
        notify(fresh);
        const focus = one('[data-homegym-quantity="1"]', row);
        if (focus && document.activeElement === opener) focus.focus({ preventScroll: true });
        return null;
      });
    } catch (error) { showError(row, error); }
  }
  function lineIdentity(line) {
    return JSON.stringify([line.variant_id, line.properties || {}, line.selling_plan_allocation || null]);
  }
  function changeQuantity(row, control, absolute) {
    const lineEl = control.closest('[data-line-key]');
    // Zeilen-Keys wechseln, wenn andere Skripte Properties setzen: notfalls ueber die Varianten-ID finden.
    const original = cart && (cart.items.find((line) => line.key === lineEl.dataset.lineKey)
      || cart.items.find((line) => str(line.variant_id) === lineEl.dataset.variantId));
    if (!original) { scheduleRefresh(); return; }
    const value = absolute === undefined ? null : str(absolute).trim();
    if (value !== null && (!/^\d+$/.test(value) || !Number.isSafeInteger(Number(value)))) {
      showError(row, text('quantity_error')); control.setAttribute('aria-invalid', 'true'); return;
    }
    control.removeAttribute('aria-invalid');
    mutate(row, async () => {
      const findLine = () => cart.items.find((line) => line.key === original.key)
        || cart.items.find((line) => lineIdentity(line) === lineIdentity(original))
        || cart.items.find((line) => line.variant_id === original.variant_id);
      cart = await readCart();
      const line = findLine();
      if (!line) return cart;
      const quantity = value === null ? Math.max(0, line.quantity + Number(control.dataset.homegymQuantity)) : Number(value);
      if (quantity === line.quantity) return cart;
      // Zeilen-Keys aendern sich (Lieferzeit-Properties, Reconcile): immer frisch lesen und per Zeilenindex aendern.
      return request(config.changeUrl, { line: cart.items.indexOf(line) + 1, quantity });
    });
  }
  async function openQuick(button) {
    const row = button.closest('[data-homegym-row]');
    try {
      const mode = await chooseRack(row, button);
      if (mode === 'cancel') return;
      const drawer = one('quick-add-drawer');
      if (!drawer || typeof drawer.handleDocumentClick !== 'function') {
        showError(row, text('error')); return;
      }
      quickContext = { row, button, drawer, mode };
      showError(row, '');
      // Same entry point + attributes as product-card. closest() also covers nested button targets.
      drawer.handleDocumentClick({ target: button });
    } catch (error) { showError(row, error); }
  }
  function returnFromQuick(event) {
    if (!quickContext || !event.target.closest('quick-add-drawer')) return;
    const context = quickContext;
    quickContext = null;
    const { row, button, drawer, mode } = context;
    if (event.detail && event.detail.variantId) preferredVariants.set(row.dataset.productId, str(event.detail.variantId));
    getProduct(row);
    // Zurueck zur Zeile erst, wenn der Drawer zu ist UND der Scroll-Lock des Themes (html.drawer-scroll-locked,
    // body position:fixed) wieder aufgehoben ist; sonst landet scrollIntoView auf dem fixierten Body bei 0
    // (Mobile-Befund Kritikrunde 1 v2.1). Poll bis 2.5 s, unabhaengig von after-close.
    let returned = false;
    const started = Date.now();
    const returnToRow = () => {
      if (returned || !row.isConnected) return;
      const themeLocked = document.documentElement.classList.contains('drawer-scroll-locked');
      // Klaviyo-Popup (klaviyo-popup-fix.js) fixiert den Body ebenfalls; solange es offen ist, warten.
      const klaviyoLocked = document.body.classList.contains('hp-klaviyo-scroll-locked') || document.body.style.position === 'fixed';
      const cartDrawer = one('cart-drawer');
      const anyOpen = drawer.getAttribute('aria-hidden') === 'false' || (cartDrawer && cartDrawer.getAttribute('aria-hidden') === 'false');
      if (anyOpen || klaviyoLocked) return;
      if (themeLocked) {
        // Der Theme-Unlock (quick-add.js, 300 ms nach before-close) blieb aus: nach 1.5 s selbst entsperren.
        if (Date.now() - started < 1500) return;
        document.documentElement.classList.remove('drawer-scroll-locked');
        document.body.style.top = '';
      }
      returned = true;
      const more = row.closest('details'); if (more) more.open = true;
      row.scrollIntoView({ behavior: 'auto', block: 'center' });
      button.focus({ preventScroll: true });
    };
    const poll = () => { returnToRow(); if (!returned && Date.now() - started < 20000) setTimeout(poll, 100); };
    drawer.close();
    setTimeout(poll, 120);
    if (mode === 'replace') mutate(row, async () => {
      cart = await readCart();
      return removeRacks(row, otherRacks(row));
    });
    else scheduleRefresh();
  }
  function unlockGallery() { document.documentElement.classList.remove('hp-homegym-lock'); }
  function openGallery(button) {
    const row = button.closest('[data-homegym-row]');
    const root = row.closest(rootSelector);
    const dialog = root && one('[data-homegym-gallery]', root);
    let images;
    try { images = JSON.parse(button.dataset.images); } catch (error) { return; }
    if (!images.length || !dialog) return;
    galleryContext = { dialog, button, images, index: 0, pointer: null };
    setText('[data-homegym-gallery-title]', rowData(row).title, dialog);
    one('[data-homegym-gallery-link]', dialog).href = button.dataset.url;
    paintImage(); dialog.showModal(); document.documentElement.classList.add('hp-homegym-lock'); dialog.addEventListener('close', unlockGallery, { once: true });
    document.body.classList.add('hp-homegym-gallery-open');
    one('[data-homegym-gallery-close]', dialog).focus();
    dialog.addEventListener('close', closeGalleryState, { once: true });
  }
  function closeGalleryState() {
    if (!galleryContext) return;
    const { button, dialog } = galleryContext;
    galleryContext = null;
    one('[data-homegym-gallery-image]', dialog).textContent = '';
    document.body.classList.remove('hp-homegym-gallery-open');
    if (button.isConnected) button.focus({ preventScroll: true });
  }
  function paintImage(delta = 0) {
    const ctx = galleryContext; if (!ctx) return;
    ctx.index = (ctx.index + delta + ctx.images.length) % ctx.images.length;
    const info = ctx.images[ctx.index];
    const container = one('[data-homegym-gallery-image]', ctx.dialog);
    const img = make('img'); img.width = 1440; img.height = 1440; img.alt = info.alt;
    img.addEventListener('error', () => { if (img.isConnected) setText('[data-homegym-image-error]', text('image_error'), ctx.dialog); });
    setText('[data-homegym-image-error]', '', ctx.dialog);
    img.src = info.src; container.textContent = ''; container.appendChild(img);
    setText('[data-homegym-image-count]', text('image_count', { index: ctx.index + 1, count: ctx.images.length }), ctx.dialog);
    all('[data-homegym-image-prev], [data-homegym-image-next]', ctx.dialog).forEach((btn) => { btn.disabled = ctx.images.length < 2; });
  }
  function openInfo(button) {
    const row = button.closest('[data-homegym-row]');
    const template = one('[data-homegym-description]', row);
    const root = row.closest(rootSelector);
    const dialog = root && one('[data-homegym-info]', root);
    if (!template || !dialog) return;
    infoContext = { dialog, button };
    setText('[data-homegym-info-title]', rowData(row).title, dialog);
    const body = one('[data-homegym-info-body]', dialog);
    body.textContent = '';
    body.appendChild(template.content.cloneNode(true));
    one('[data-homegym-info-link]', dialog).href = one('.hp-homegym-row__title', row).href;
    dialog.showModal();
    dialog.classList.add('is-open');
    document.documentElement.classList.add('hp-homegym-lock');
    one('.hp-popup__card', dialog).focus({ preventScroll: true });
    dialog.addEventListener('close', () => {
      dialog.classList.remove('is-open');
      document.documentElement.classList.remove('hp-homegym-lock');
      infoContext = null;
      if (button.isConnected) button.focus({ preventScroll: true });
    }, { once: true });
  }
  function updateLayout() {
    scrollFrame = null;
    all('[data-homegym-steps]').forEach((root) => {
      const sticky = one('[data-homegym-sticky]', root);
      if (!sticky) return;
      const height = `${sticky.offsetHeight}px`;
      if (root.style.getPropertyValue('--hp-homegym-sticky-height') !== height) {
        root.style.setProperty('--hp-homegym-sticky-height', height);
      }
      const top = parseFloat(getComputedStyle(sticky).top) || 0;
      // Last heading to cross the lower edge of the sticky head, in document order.
      const steps = all('[data-homegym-step]', root);
      let current = steps[0];
      steps.forEach((step) => {
        if (one('h2', step).getBoundingClientRect().top <= top + sticky.offsetHeight + 1) current = step;
      });
      if (!current) return;
      all('[data-homegym-chip]', root).forEach((chip) => {
        if (chip.dataset.homegymChip !== current.dataset.homegymStep) chip.removeAttribute('aria-current');
        else if (chip.getAttribute('aria-current') !== 'step') {
          chip.setAttribute('aria-current', 'step');
        }
      });
    });
  }
  function scheduleLayout() {
    if (!one('[data-homegym-steps]')) return;
    if (!scrollFrame) scrollFrame = requestAnimationFrame(updateLayout);
  }
  function init() {
    const configEl = one('[data-homegym-config]');
    if (layoutObserver) layoutObserver.disconnect();
    if (headerObserver) headerObserver.disconnect();
    if (!configEl) return;
    config = JSON.parse(configEl.textContent);
    paintDeliveryTiers();
    firstRenderDone = false;
    rows().forEach((row) => {
      const data = rowData(row);
      // Feed the existing warehouse cart reconciler, including products picked by editor blocks.
      if (data.buckets) window.__hpb = Object.assign(window.__hpb || {}, data.buckets);
    });
    if (one('[data-homegym-steps]')) {
      if ('ResizeObserver' in window) {
        layoutObserver = new ResizeObserver(scheduleLayout);
        all('[data-homegym-sticky], [data-homegym-steps], .cc-header--sticky').forEach((el) => layoutObserver.observe(el));
      }
      // main.js updates --header-height after menu/search/header transitions.
      headerObserver = new MutationObserver(scheduleLayout);
      headerObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
      scheduleLayout();
    }
    if (cart) render(cart);
    scheduleRefresh();
  }
  document.addEventListener('click', (event) => {
    // Leave the main product form and all unrelated theme controls alone.
    if (!event.target.closest(rootSelector) && !event.target.closest(dialogSelector)) return;
    const summary = event.target.closest('[data-homegym-accessories-toggle] > summary');
    if (summary) manualAccessories.add(summary.parentElement);
    const target = event.target.closest('button, a, [data-homegym-gallery], [data-homegym-info]');
    if (!target) return;
    const info = target.closest('[data-homegym-read-more]');
    if (info) { openInfo(info); return; }
    if (infoContext && (target.closest('[data-homegym-info-close]') || event.target === infoContext.dialog)) {
      infoContext.dialog.close(); return;
    }
    const choose = target.closest('[data-homegym-choose]');
    if (choose) { event.preventDefault(); event.stopImmediatePropagation(); openQuick(choose); return; }
    const notifyButton = target.closest('[data-homegym-notify]');
    if (notifyButton) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const row = notifyButton.closest('[data-homegym-row]');
      const data = rowData(row);
      showError(row, '');
      const rr = window._RestockRocket;
      if (!rr || typeof rr.openModal !== 'function') { showError(row, text('notify_unavailable')); return; }
      rr.openModal({
        id: Number(row.dataset.productId), title: data.title, handle: data.handle,
        variants: data.variants.map((v) => ({ id: v.id, title: v.title, available: v.available }))
      }, Number(row.dataset.fixedVariant || row.dataset.variantId));
      return;
    }
    const choice = target.closest('[data-homegym-choice]');
    if (choice) { finishChoice(choice.dataset.homegymChoice); return; }
    const add = target.closest('[data-homegym-add]');
    if (add && !add.disabled && add.closest(rootSelector)) { event.preventDefault(); addRow(add.closest('[data-homegym-row]'), add); return; }
    const quantity = target.closest('[data-homegym-quantity]');
    if (quantity && !quantity.disabled) { changeQuantity(quantity.closest('[data-homegym-row]'), quantity); return; }
    const zoom = target.closest('[data-homegym-zoom]');
    if (zoom && !zoom.disabled) { openGallery(zoom); return; }
    if (galleryContext && target.closest('[data-homegym-gallery-close]')) galleryContext.dialog.close();
    else if (target.closest('[data-homegym-image-prev]')) paintImage(-1);
    else if (target.closest('[data-homegym-image-next]')) paintImage(1);
    else if (galleryContext && event.target === galleryContext.dialog) galleryContext.dialog.close();
  }, true);
  document.addEventListener('change', (event) => {
    if (!event.target.closest(rootSelector)) return;
    const input = event.target.closest('[data-homegym-quantity-input]');
    if (input) changeQuantity(input.closest('[data-homegym-row]'), input, input.value);
  });
  // Typed quantities behave like the theme's quantity-input.js: click selects the number, Enter applies it.
  document.addEventListener('focusin', (event) => {
    const input = event.target.closest && event.target.closest('[data-homegym-quantity-input]');
    if (input && input.closest(rootSelector) && window.matchMedia('(pointer: fine)').matches) input.select();
  });
  document.addEventListener('keydown', (event) => {
    if (choiceContext && event.key === 'Escape') { event.preventDefault(); finishChoice('cancel'); }
    if (event.key === 'Enter' && event.target.closest && event.target.closest('[data-homegym-quantity-input]') && event.target.closest(rootSelector)) {
      event.preventDefault(); event.target.blur(); event.target.focus(); return;
    }
    if (!galleryContext || !event.target.closest('[data-homegym-gallery]')) return;
    if (event.key === 'ArrowLeft') { event.preventDefault(); paintImage(-1); }
    if (event.key === 'ArrowRight') { event.preventDefault(); paintImage(1); }
    // The native modal dialog handles Escape and focus containment.
  });
  document.addEventListener('pointerdown', (event) => {
    if (galleryContext && event.target.closest('[data-homegym-gallery-image]')) galleryContext.pointer = { x: event.clientX, y: event.clientY };
  });
  document.addEventListener('pointerup', (event) => {
    if (!galleryContext || !galleryContext.pointer) return;
    const start = galleryContext.pointer; galleryContext.pointer = null;
    if (!event.target.closest('[data-homegym-gallery-image]')) return;
    const dx = event.clientX - start.x; const dy = event.clientY - start.y;
    if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) paintImage(dx < 0 ? 1 : -1);
  });
  ['on:cart:add', 'on:line-item:change'].forEach((name) => document.addEventListener(name, (event) => {
    returnFromQuick(event); scheduleRefresh(event);
  }));
  document.addEventListener('on:quickbuy:after-close', (event) => {
    if (quickContext && event.target === quickContext.drawer && quickContext.drawer.getAttribute('aria-hidden') !== 'false') quickContext = null;
  });
  ['dispatch:cart-drawer:refresh', 'on:bfcache:load-restore'].forEach((name) => document.addEventListener(name, scheduleRefresh));
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', () => {
    if (choiceContext) finishChoice('cancel');
    if (galleryContext) galleryContext.dialog.close();
    if (infoContext) infoContext.dialog.close();
    setTimeout(init, 0);
  });
  window.addEventListener('pageshow', (event) => { if (event.persisted) scheduleRefresh(); });
  window.addEventListener('scroll', scheduleLayout, { passive: true });
  window.addEventListener('resize', scheduleLayout, { passive: true });
  document.addEventListener('toggle', scheduleLayout, true);
  init();
})();
